import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Check, X, FileText } from "lucide-react";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import axiosInstance from "../../axiosInstance";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import { ErrorState } from "../../components/ui/Feedback";
import { sortedLeaveDates, toInputValue, formatDateShort } from "./leaveUtils";
import "./Fallback.scss";

const API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/leave-history/fallback`;

// Same shape for requests and reports. Statuses stay plain strings here (the
// table renders them with <StatusCard>), so rows remain searchable and
// sortable, and nothing is rendered twice.
const normalizeItem = (item) => {
  const dates = sortedLeaveDates(item.leaveDates);
  return {
    leaveHistoryId: item.leaveHistoryId,
    fromDate: dates.length > 0 ? toInputValue(dates[0]) : "",
    toDate: dates.length > 0 ? toInputValue(dates[dates.length - 1]) : "",
    fallbackFrom: item.fallBackFrom,
    role: item.role,
    departments: item.departments,
    responsibility: item.responsibility || "N/A",
    fallbackApprovalStatus: item.fallbackApprovalStatus,
    leaveStatus: item.leaveStatus,
  };
};

const isOk = (response) =>
  response.data.status === 200 || response.data.status === 0;

function Fallback({ onRequestProcessed }) {
  const [requests, setRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const [requestsError, setRequestsError] = useState(false);
  const [reportsError, setReportsError] = useState(false);
  const [rejecting, setRejecting] = useState(null); // request row awaiting confirmation
  const [busyId, setBusyId] = useState(null);

  const fetchRequests = useCallback(async () => {
    setRequestsError(false);
    try {
      const response = await axiosInstance.get(`${API}/pending`);
      if (isOk(response)) setRequests(response.data.data.map(normalizeItem));
    } catch (error) {
      console.error("Error fetching fallback requests", error);
      setRequestsError(true);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setLoadingReports(true);
    setReportsError(false);
    try {
      const response = await axiosInstance.get(`${API}/all`);
      if (isOk(response)) setReports(response.data.data.map(normalizeItem));
    } catch (error) {
      console.error("Error fetching fallback reports", error);
      setReportsError(true);
    } finally {
      setLoadingReports(false);
    }
  }, []);

  useEffect(() => {
    setLoadingRequests(true);
    fetchRequests().finally(() => setLoadingRequests(false));
  }, [fetchRequests]);

  const toggleReports = () => {
    const next = !showReports;
    setShowReports(next);
    if (next) fetchReports();
  };

  // action: "approve" | "reject"
  const processRequest = async (action, leaveHistoryId) => {
    setBusyId(leaveHistoryId);
    try {
      await axiosInstance.put(`${API}/${action}/${leaveHistoryId}`);
      toast.success(
        action === "approve"
          ? "Fallback request is approved!"
          : "Fallback request is rejected!",
      );
      await fetchRequests();
      if (showReports) fetchReports();

      // Let the parent update its notification count.
      if (onRequestProcessed) onRequestProcessed();
    } catch (error) {
      console.error(`Error trying to ${action} fallback request`, error);
      toast.error(`Failed to ${action} fallback request`);
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    await processRequest("reject", rejecting.leaveHistoryId);
    setRejecting(null);
  };

  const dateRender = (accessor) => (row) => formatDateShort(row[accessor]);

  const baseColumns = [
    {
      header: "From Date",
      accessor: "fromDate",
      render: dateRender("fromDate"),
    },
    { header: "To Date", accessor: "toDate", render: dateRender("toDate") },
    { header: "Fallback From", accessor: "fallbackFrom" },
    { header: "Role", accessor: "role" },
    { header: "Department", accessor: "departments" },
    { header: "Responsibility", accessor: "responsibility" },
    {
      header: "Approval Status",
      accessor: "fallbackApprovalStatus",
      render: (row) => <StatusCard status={row.fallbackApprovalStatus} />,
    },
    {
      header: "Leave Status",
      accessor: "leaveStatus",
      render: (row) => <StatusCard status={row.leaveStatus} />,
    },
  ];

  const requestColumns = [
    ...baseColumns,
    {
      header: "Actions",
      key: "actions",
      sortable: false,
      align: "right",
      render: (row) => {
        const busy = busyId === row.leaveHistoryId;
        return (
          <div className="fallback-actions">
            <button
              type="button"
              className="hr-btn hr-btn--secondary fallback-btn fallback-btn--approve"
              disabled={busy}
              aria-label={`Approve fallback request from ${row.fallbackFrom}`}
              onClick={() => processRequest("approve", row.leaveHistoryId)}
            >
              <Check size={14} aria-hidden="true" />
              Approve
            </button>
            <button
              type="button"
              className="hr-btn hr-btn--secondary fallback-btn fallback-btn--reject"
              disabled={busy}
              aria-label={`Reject fallback request from ${row.fallbackFrom}`}
              onClick={() => setRejecting(row)}
            >
              <X size={14} aria-hidden="true" />
              Reject
            </button>
          </div>
        );
      },
    },
  ];

  const retryRequests = () => {
    setLoadingRequests(true);
    fetchRequests().finally(() => setLoadingRequests(false));
  };

  return (
    <div className="fallback-page">
      {requestsError ? (
        <section className="hr-card">
          <ErrorState
            title="Couldn't load pending fallback requests"
            message="Check your connection and try again."
            onRetry={retryRequests}
          />
        </section>
      ) : (
        <GeneralTable
          columns={requestColumns}
          data={requests}
          loading={loadingRequests}
          rowKey={(row) => row.leaveHistoryId}
          className="fallback-table"
          title="Pending Fallback Requests"
          titleActions={
            <button
              type="button"
              className="hr-btn hr-btn--secondary"
              aria-pressed={showReports}
              onClick={toggleReports}
            >
              <FileText size={16} aria-hidden="true" />
              {showReports ? "Hide reports" : "Show reports"}
            </button>
          }
          emptyStateTitle="No pending fallback requests"
          emptyStateSubtitle="When a colleague asks you to cover for them, it will show up here."
        />
      )}

      {showReports &&
        (reportsError ? (
          <section className="hr-card">
            <ErrorState
              title="Couldn't load fallback reports"
              message="Check your connection and try again."
              onRetry={fetchReports}
            />
          </section>
        ) : (
          <GeneralTable
            columns={baseColumns}
            data={reports}
            loading={loadingReports}
            rowKey={(row) => row.leaveHistoryId}
            className="fallback-table"
            title="Fallback Reports"
            emptyStateTitle="No fallback reports"
            emptyStateSubtitle="Past and current fallback assignments will be listed here."
          />
        ))}

      <ConfirmationDialog
        open={Boolean(rejecting)}
        title="Reject this fallback request?"
        message={
          rejecting
            ? `You'll decline to cover for ${rejecting.fallbackFrom || "this colleague"} (${formatDateShort(
                rejecting.fromDate,
              )} – ${formatDateShort(rejecting.toDate)}). The request will be marked as rejected.`
            : ""
        }
        confirmLabel="Reject request"
        busy={busyId !== null}
        onConfirm={confirmReject}
        onCancel={() => setRejecting(null)}
      />
    </div>
  );
}

export default Fallback;
